import React, { useContext, useCallback } from 'react';
import { DSFastList } from '@elliemae/ds-fast-list';
import { type SizingProps, styled } from '@elliemae/ds-system';
import { Grid } from '@elliemae/ds-grid';
import { useOwnerProps } from '@elliemae/ds-props-helpers';
import { StyledListWrapper, StyledNoResultsWrapper } from './styled.js';
import { ComboboxDataTestid } from '../../ComboboxDataTestids.js';
import { ComboBoxContext } from '../../ComboBoxCTX.js';
import { ItemRenderer } from './useItemRenderer.js';
import { LoadingContainer } from './LoadingContainer.js';
import { SkeletonContainer } from './SkeletonContainer.js';
import { useOnElementResize } from '../../utils/useOnElementResize.js';
import { DSFormComboboxName, FORM_COMBOBOX_SLOTS } from '../../constants/index.js';

const StyledFastList = styled(DSFastList, {
  name: DSFormComboboxName,
  slot: FORM_COMBOBOX_SLOTS.FAST_LIST,
})``;

const StyledItemsWrapper = styled(Grid)<{
  maxHeight: SizingProps['maxHeight'];
}>`
  max-height: ${({ maxHeight }) => (maxHeight ? `${String(maxHeight)}px` : '300px')};
`;

const estimatedSize = () => 32;

export const MenuList = (): JSX.Element => {
  const {
    props,
    props: { isLoading, menuMinWidth, noOptionsMessage, menuMaxHeight, onCreate, inline, isSkeleton },
    controlsWrapperRef,
    listRef,
    wrapperListRef,
    correctOptions: filteredOptions,
    instanceUid,
  } = useContext(ComboBoxContext);

  const { getOwnerProps } = useOwnerProps(props);
  const { width } = useOnElementResize(controlsWrapperRef);

  const preventLoseInputFocus: React.MouseEventHandler = useCallback((e) => {
    e.preventDefault();
  }, []);

  let content: JSX.Element;

  if (isSkeleton) {
    content = <SkeletonContainer instanceUid={instanceUid} getOwnerProps={getOwnerProps} />;
  } else if (isLoading) {
    content = <LoadingContainer getOwnerProps={getOwnerProps} />;
  } else if ((filteredOptions?.length ?? 0) > 0 || onCreate) {
    content = (
      <StyledItemsWrapper maxHeight={menuMaxHeight} id={`combo-listbox-${instanceUid}`}>
        <StyledFastList
          dsFastlistWrapperList={{
            role: 'listbox',
            'data-testid': ComboboxDataTestid.LIST,
            'aria-label': 'listbox',
          }}
          dsFastlistItem={{ role: 'none' }}
          actionRef={listRef}
          count={filteredOptions.length}
          ItemRenderer={ItemRenderer}
          extraItemProps={{ itemList: filteredOptions }}
          getId={(index: number) => filteredOptions[index].dsId}
          estimateSize={estimatedSize}
          getOwnerProps={getOwnerProps}
        />
      </StyledItemsWrapper>
    );
  } else {
    content = (
      <StyledNoResultsWrapper
        id={`combo-listbox-${instanceUid}`}
        data-testid={ComboboxDataTestid.NO_MATCHES_FOUND}
        role="alert"
      >
        {noOptionsMessage}
      </StyledNoResultsWrapper>
    );
  }

  return (
    <StyledListWrapper
      innerRef={wrapperListRef}
      inline={inline}
      onMouseDown={preventLoseInputFocus}
      width={width}
      minWidth={menuMinWidth}
    >
      {content}
    </StyledListWrapper>
  );
};
